import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';
import { DashboardCard, UnifiedCardGrid } from '../components/unified';

/**
 * FarmOverviewGrid — "Mein Betrieb" tab.
 * 8 kacheln showing real data from /dashboard/summary.
 * Uses DashboardCard + UnifiedCardGrid from Unified UI system.
 *
 * See: UNIFIED_UI_ARCHITECTURE.md §5.4, §5.5, §7 Phase 2
 */
export default function FarmOverviewGrid({ dashboard, tier, onCardTap, token, user, isFeatureEnabled }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/dashboard/summary')
      .then((res) => { const d = res?.data ?? res; if (d) setSummary(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const s = summary || {};
  const f = s.fields || {};
  const tk = s.tasks || {};
  const st = s.storage || {};
  const mp = s.market_prices || {};
  const w = s.weather || {};
  const cm = s.contribution_margin || {};
  const sub = s.subsidies || {};
  const co = s.compliance || {};
  const notes = s.notes || {};

  // Truncate long names for kachel display (max ~20 chars)
  const shortName = (name, max = 20) => name && name.length > max ? name.slice(0, name.indexOf(' ', 10) > 0 ? name.indexOf(' ', 10) : max) : name;

  const allKacheln = [
    {
      icon: <span className="text-2xl">🌱</span>,
      title: t('dashboard.fields'),
      metric: loading ? '...' : (f.count > 0 ? `${f.count} · ${Math.round(f.total_ha || 0)} ha` : t('dashboard.zeroFields')),
      subtitle: f.count > 0 && f.next_task_field ? `${t('dashboard.nextShort')}: ${shortName(f.next_task_field)}` : null,
      alert: f.alert,
      onClick: () => onCardTap('fields'),
      loading,
    },
    {
      icon: <span className="text-2xl">✅</span>,
      title: t('task.weeklyTitle'),
      metric: loading ? '...' : (tk.open > 0 ? `${tk.open} ${t('dashboard.open')}` : t('dashboard.zeroTasks')),
      subtitle: tk.today > 0 ? t('dashboard.tasksToday', { count: tk.today }) : null,
      alert: tk.alert,
      onClick: () => onCardTap('weekly'),
      loading,
    },
    {
      featureKey: 'storage_tile',
      icon: <span className="text-2xl">🏭</span>,
      title: t('storage.title'),
      metric: loading ? '...' : `${(st.total_value_eur || 0).toLocaleString()} €`,
      subtitle: `${st.product_count || 0} ${t('dashboard.products')}`,
      alert: st.alert,
      onClick: () => onCardTap('storage'),
      loading,
    },
    {
      icon: <span className="text-2xl">📈</span>,
      title: t('dashboard.market'),
      metric: loading ? '...' : (mp.top_crop ? `${mp.top_crop} ${mp.top_price || 0} €/t` : t('dashboard.zeroMarket')),
      subtitle: mp.top_trend_pct ? `${mp.top_trend_pct > 0 ? '+' : ''}${mp.top_trend_pct}% ↑` : null,
      onClick: () => onCardTap('market'),
      loading,
    },
    {
      icon: <span className="text-2xl">☀️</span>,
      title: t('dashboard.weather'),
      metric: loading ? '...' : (w.temp_c != null ? `${w.temp_c}°C · ${w.description || '—'}` : t('dashboard.zeroWeather')),
      subtitle: w.total_fields > 0 ? t('dashboard.sprayable', { count: w.sprayable_fields, total: w.total_fields }) : null,
      onClick: () => onCardTap('weather'),
      loading,
    },
    {
      featureKey: 'finance_tile',
      icon: <span className="text-2xl">💶</span>,
      title: t('finance.title'),
      metric: loading ? '...' : (cm.avg_db_per_ha > 0 ? `${cm.avg_db_per_ha.toLocaleString()} €/ha` : '—'),
      subtitle: cm.avg_db_per_ha <= 0 ? t('dashboard.enterData') : null,
      onClick: () => onCardTap('finance'),
      loading,
    },
    {
      icon: <span className="text-2xl">🎯</span>,
      title: t('dashboard.subsidies'),
      metric: loading ? '...' : (sub.days_until > 0 ? `${sub.days_until} ${t('dashboard.days')}` : t('dashboard.noCurrentDeadline')),
      subtitle: sub.days_until > 0 ? shortName(sub.next_deadline, 25) : null,
      alert: sub.days_until > 0 && sub.days_until < 60 ? { text: t('dashboard.deadlineRunning'), severity: 'warning' } : null,
      onClick: () => onCardTap('subsidies'),
      loading,
    },
    {
      icon: <span className="text-2xl">📝</span>,
      title: t('brainDump.title', 'Notizen'),
      metric: loading ? '...' : (notes.count > 0
        ? `${notes.count} ${t('dashboard.notes', 'Notizen')}`
        : t('dashboard.zeroNotes', 'Noch keine Notizen')),
      subtitle: null,
      onClick: () => onCardTap('brain-dump'),
      loading,
    },
    {
      featureKey: 'compliance_tile',
      icon: <span className="text-2xl">🛡️</span>,
      title: t('dashboard.compliance'),
      metric: loading ? '...' : (co.gloez_total > 0 ? `${co.gloez_ok}/${co.gloez_total} GLÖZ ✅` : 'OK'),
      subtitle: co.n_balance_ok ? t('dashboard.nBalanceOk') : t('dashboard.nBalanceWarning'),
      alert: co.alert,
      onClick: () => onCardTap('compliance'),
      loading,
    },
  ];

  const kacheln = allKacheln.filter(k => !k.featureKey || isFeatureEnabled?.(k.featureKey));

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <div className="max-w-2xl mx-auto px-4 py-5">
        <h2 className="text-lg font-semibold text-[var(--aif-text-primary)] mb-4">{t('today.farmOverview')}</h2>
        <UnifiedCardGrid columns={{ desktop: 3, tablet: 2, mobile: 1 }}>
          {kacheln.map((k, i) => (
            <DashboardCard key={i} {...k} />
          ))}
        </UnifiedCardGrid>
      </div>
    </div>
  );
}
